import { Command as CommandPrimitive } from 'cmdk'
import { useCallback, useRef, useState, type KeyboardEvent } from 'react'
import { Command } from '~/components/ui'
import { cn } from '~/utils'

export type Option = Record<'value' | 'label', string> & Record<string, string>

type AddressAutoCompleteProps = {
  options: Option[]
  address?: string
  onSelectOption: (value: Option) => void
  onInputChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  inputClassName?: string
}

export const AddressAutoComplete = ({
  options,
  placeholder,
  address,
  onSelectOption,
  onInputChange,
  disabled,
  inputClassName,
}: AddressAutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setOpen] = useState(false)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current
      if (!input) {
        return
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setOpen(true)
      }

      // This is not a default behavior of the <input /> field
      if (event.key === 'Enter' && input.value !== '') {
        const optionToSelect = options.find(
          (option) => option.label === input.value
        )
        if (optionToSelect) {
          onSelectOption(optionToSelect)
        }
      }

      if (event.key === 'Escape') {
        input.blur()
      }
    },
    [isOpen, options, onSelectOption]
  )

  const handleBlur = useCallback(() => {
    setOpen(false)
  }, [])

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      onSelectOption(selectedOption)

      // This is a hack to prevent the input from being focused after the user selects an option
      // We can call this hack: "The next tick"
      setTimeout(() => {
        inputRef?.current?.blur()
      }, 0)
    },
    [onSelectOption]
  )

  return (
    <CommandPrimitive onKeyDown={handleKeyDown} shouldFilter={false}>
      <div>
        <CommandPrimitive.Input
          ref={inputRef}
          value={address}
          onValueChange={(value) => {
            onInputChange(value)
          }}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            inputClassName
          )}
        />
      </div>
      <div className="relative mt-1">
        {options.length > 0 && (
          <div
            className={cn(
              'absolute top-1 z-10 w-full rounded-xl outline-none animate-in fade-in-0 zoom-in-95',
              'rounded-md bg-background text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
              isOpen ? 'block' : 'hidden'
            )}>
            <Command.List>
              <Command.Group>
                {options.map((option) => {
                  return (
                    <Command.Item
                      key={option.value}
                      value={option.label}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className={cn(
                        'flex w-full items-center gap-2 py-2 aria-selected:bg-primary aria-selected:text-white'
                      )}>
                      {option.label}
                    </Command.Item>
                  )
                })}
              </Command.Group>
            </Command.List>
          </div>
        )}
      </div>
    </CommandPrimitive>
  )
}
